import { array, option, ord, string } from 'fp-ts';
import { flow, pipe } from 'fp-ts/function';
import { useFormContext } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { Address, useRepositoryQuery } from '@mews-commander/data-access';
import { EMPTY_ADDRESS_FORM_VALUES } from '../constants';
import { addressToFormValues } from '../utils';
import { ChangeBillingDetailsFormValues } from '../types';

const shouldDirty = { shouldDirty: true };

const updatedUtcOrd = pipe(
    string.Ord,
    ord.contramap(({ updatedUtc }: Address) => updatedUtc ?? ''),
    ord.reverse
);

const getPrimaryAddress = flow(
    array.filter(({ isComplete }: Address) => isComplete),
    array.sort(updatedUtcOrd),
    array.head
);

export const useSelectedAccountAddress = (
    currentAssociatedAccountId: ChangeBillingDetailsFormValues['associatedAccount']['id']
) => {
    const { setValue } = useFormContext<ChangeBillingDetailsFormValues>();
    const [hasResultBeenSelected, toggleResultSelectionFlag] = useState(false);

    const result = useRepositoryQuery(
        'accounts',
        'getAll',
        { accountIds: currentAssociatedAccountId ? [currentAssociatedAccountId] : [], extent: { addresses: true } },
        // cache time is 0 because we currently have no way to invalidate the cache when the account's address is updated
        {
            enabled: !!currentAssociatedAccountId,
            gcTime: 0,
            select: ({ addresses }) =>
                pipe(
                    addresses,
                    option.fromNullable,
                    option.chain(getPrimaryAddress),
                    option.match(() => EMPTY_ADDRESS_FORM_VALUES, addressToFormValues)
                ),
        }
    );

    useEffect(() => {
        if (hasResultBeenSelected && result.isSuccess) {
            setValue('associatedAccount.address', result.data, shouldDirty);
        }
    }, [hasResultBeenSelected, result.isSuccess, result.data, setValue]);

    return {
        result,
        toggleResultSelectionFlag,
    };
};
